import { CheckCircle2, Zap } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useContextStore } from '@/lib/session/context-store'
import { Day0IdentityHeader } from './Day0IdentityHeader'
import { Day0StepperCard } from './Day0StepperCard'
import { Day0NextStepCard } from './Day0NextStepCard'
import { Day0CurrentStageCard } from './Day0CurrentStageCard'
import { Day0PeopleCard } from './Day0PeopleCard'
import { Day0PostImportExplainer, type Day0ExplainerStep } from './Day0PostImportExplainer'
import { Day0LiveDashboardTile } from './Day0LiveDashboardTile'
import type { AgencyActivationState, AgencyActivationStage } from '@/lib/hooks/useMyAgencyActivationState'

interface AgencyActivationBoardProps {
  state: AgencyActivationState
}

/**
 * Machine key → human button label for an agency stage's primaryAction, mirroring the CTA_LABELS
 * lookup already established in ActivationStageItem.tsx for the client. Unknown keys fall back to
 * the raw primaryAction rather than a fabricated label — same behaviour as that precedent.
 */
const AGENCY_CTA_LABELS: Record<string, string> = {
  review_agreement: 'Review & sign',
}

interface AgencyStageCopy {
  instructionHeading: string
  instructionBody: string
  /** null = nothing more honest to add for this stage. */
  footnoteText: string | null
}

/**
 * Stage-keyed instructional copy for the WHAT TO DO NOW card, keyed by stage.key (stable,
 * server-assigned). Covers all three known agency stage keys (`agency_created`, `agreement`, `review` —
 * BE removed `go_live` in ADG-T01, so there is no entry for it) so an unrecognised future key falls
 * back to the generic message below rather than rendering nothing.
 *
 * Every entry is real copy, not null — C13/T0062 found `agency_created` CAN be the resolved active
 * stage (see `activeStageIndex` below) whenever `current_stage` is still 1 and nothing else is
 * NEEDED yet, so it needed honest copy of its own rather than falling through to the generic
 * fallback.
 */
const AGENCY_STAGE_COPY: Record<string, AgencyStageCopy | null> = {
  // C13/T0062 — was `null` (falling through to FALLBACK_STAGE_COPY's "complete the steps below,"
  // which names a checklist that doesn't exist). Given real, catalog-matching copy instead — this
  // stage genuinely can be the resolved "active" one (current_stage === 1, nothing yet NEEDED
  // anywhere), and the fallback text was wrong for it, not merely generic.
  agency_created: {
    instructionHeading: 'Setting up your agency account',
    instructionBody: "We're finishing setup on our end — this will just take a moment.",
    footnoteText: null,
  },

  agreement: {
    instructionHeading: 'Sign your agency agreement',
    instructionBody: 'Review and sign the agreement to continue activating your agency account.',
    footnoteText: 'Once you sign, your application moves to platform review.',
  },

  review: {
    instructionHeading: 'Waiting for platform review',
    instructionBody:
      "A platform admin is reviewing your agency application. You'll receive an email once it's approved — there's nothing you need to do right now.",
    footnoteText: null,
  },

}

/**
 * C13/T0062 — the agreement stage's OWN `state` is driven by the real agreement/papering status
 * (`agreementStepState` on BE), independently of `current_stage`'s ordinal — by design (T10): the
 * agency's own MSA can still be mid-drafting even once `current_stage` has already advanced to the
 * agreement ordinal. `AGENCY_STAGE_COPY.agreement` above assumes NEEDED (there is something to
 * sign); when the resolved active stage IS 'agreement' but its own state is NOT NEEDED, that copy
 * would show a "sign your agreement" heading with no CTA underneath it — the same class of defect
 * this task exists to close, just one stage over. This is the honest alternative: what the user is
 * waiting for, and who is doing it (the platform, drafting the document).
 */
const AGREEMENT_PREPARING_COPY: AgencyStageCopy = {
  instructionHeading: "We're preparing your agreement",
  instructionBody:
    "The platform is drafting your agency agreement. You'll be able to review and sign it here once it's ready — there's nothing you need to do right now.",
  footnoteText: null,
}

/**
 * T0075 (owner report) — the agreement stage's BLOCKED state collapsed TWO different reasons into
 * one branch: "not yet drafted" (AGREEMENT_PREPARING_COPY, correct there) and "the agency has
 * ALREADY signed and is waiting on the platform" (AWAITING_COUNTERSIGNATURE) — which rendered the
 * SAME "we're preparing/you'll be able to review and sign it" copy, describing a completed step as
 * unstarted and promising a sign action that has already happened.
 *
 * AFO (BE AFO-T13, handover Rev 5): the discriminator is now the agreement step's `reason`
 * (`AWAITING_COUNTERSIGNATURE`). BE nulled `primaryAction` for every held case — the old waiting label that
 * lived there (ADG-T01) is never sent any more — so
 * comparing `primaryAction` to that label would silently make this branch dead and bring the T0075
 * defect straight back. Held copy is gated on `reason`, never on `primaryAction` or `state`.
 *
 * Copy honours the owner's wording literally as two commitments, not a status: "nothing is pending
 * on you" and "we will tell you when review is done and you are ready to proceed" — deliberately
 * stronger than a bare "waiting on the platform" status label (cf. `agreement-progress.ts`'s rail).
 */

const AGREEMENT_AWAITING_COUNTERSIGNATURE_COPY: AgencyStageCopy = {
  instructionHeading: 'Nothing pending on you',
  instructionBody:
    "You've signed your agency agreement. There's nothing more for you to do — we'll let you know as soon as review is done and you're ready to proceed.",
  footnoteText: null,
}

/**
 * ADG-T02 — contract-svc's recipient-role literal for the SIDE OTHER than this org on its own
 * establishing agreement. This org's own role is always `CLIENT` (BE's `OWN_RECIPIENT_ROLE`,
 * `own-agreement-status.service.ts`) regardless of persona, so the counterparty — the platform — is
 * always `VENDOR`. Never derived from persona: that works for exactly two parties and breaks for any
 * third viewer, which is the whole reason the payload carries `role` instead of a bare boolean.
 */
const COUNTERPARTY_RECIPIENT_ROLE = 'VENDOR'

/**
 * ADG-T02 — shown ALONGSIDE the existing sign prompt when the platform's half has been OBSERVED.
 * Presence-only: this string exists to be shown, never negated. There is no "has not signed yet"
 * counterpart — an absent half means say NOTHING about the counterparty, per the BE contract
 * (`AgencyActivationStage.signatures`'s own docblock). Never render this from an empty/absent list.
 */
const AGREEMENT_COUNTERPARTY_SIGNED_NOTE = 'The other party has already signed.'

// No longer "complete the steps below" (there are no steps below — Day0CurrentStageCard's
// checklist section is a real BE gap for CLIENTS, not something agencies will ever have). Kept
// generic and honest for a genuinely unrecognised future stage key, which every currently-known
// key now has real copy for, so this is a true last resort, not a routinely-hit default.
const FALLBACK_STAGE_COPY: AgencyStageCopy = {
  instructionHeading: 'Activating your agency account',
  instructionBody: 'Your agency onboarding is in progress.',
  footnoteText: null,
}

/** Translation of the client's "WHAT HAPPENS AFTER IMPORT?" — the agency's centrepiece is signing,
 * not uploading, so what follows is review then go-live, not a third "live dashboard" step (that's
 * covered by the separate Day0LiveDashboardTile below, reused unmodified — its copy is already
 * persona-neutral). */
const AGENCY_POST_SIGN_STEPS: readonly Day0ExplainerStep[] = [
  {
    icon: CheckCircle2,
    label: 'Review',
    body: 'A platform admin checks your submitted agreement and details.',
  },
  {
    icon: Zap,
    label: 'Go live',
    body: 'Once approved, your agency account activates and the full dashboard opens up.',
  },
]

export function AgencyActivationBoard({ state }: AgencyActivationBoardProps) {
  const navigate = useNavigate()
  const memberships = useContextStore((s) => s.memberships)
  const active = useContextStore((s) => s.active)

  const orgName =
    memberships.find((m) => m.contextTenantId === active?.tenantId)?.displayName ??
    active?.tenantId ??
    '—'

  // currentStage is 1-indexed; null means the activation record exists but no stage has started
  // yet. Treat null as genuinely unknown — do not coerce to 1, that would assert progress that
  // hasn't happened. This is a DELIBERATE fork from Day0LaunchScreen (which defaults to 1): the
  // client's status is never observed with an unresolved stage by the time it reaches this screen,
  // the agency's is (see AT_NULL_STAGE in the test file) — do not lose that honesty.
  if (state.currentStage === null) {
    return (
      <div data-testid="agency-activation-board" className="flex flex-col gap-6 p-6 max-w-3xl mx-auto">
        <Day0IdentityHeader
          orgName={orgName}
          ownerName={null}
          createdAt={state.submittedAt}
          ctaLabel={null}
          onCta={() => {}}
        />
        <Day0NextStepCard
          instructionHeading="Your agency account is pending activation"
          instructionBody="Activation steps will appear shortly."
          primaryAction={null}
          secondaryAction={null}
          secondaryUrl={null}
          tertiaryLinks={null}
          acceptedFormats={null}
        />
      </div>
    )
  }

  // C13/T0062 — ONE SOURCE OF TRUTH for "which stage is active," used for EVERYTHING below (the
  // stepper's number, the current-stage card's label, and the sign-CTA gate). Previously this board
  // indexed `state.stages` by `state.currentStage` directly — a POSITION read. But the agreement
  // stage's own `state` is independently driven by the agency's real agreement/papering status
  // (BE's T10 design: `agency-activation-state.service.ts`), advanced by a SEPARATE async process
  // than whatever advances `current_stage` itself (`activation-write.service.ts`'s transition
  // events). The two can legitimately disagree — verified live (2026-09-08) against "Test Acme
  // Agency Local 3": current_stage had not yet advanced past 1 while the agreement stage's own
  // status had already gone NEEDED, and the two other places already reading per-stage `state`
  // (`Day0StepperCard`'s label, `Day0HorizontalStepper`'s rail) correctly showed "Agreement" while
  // this board's OWN position-based read still pointed at "Agency created" — three-way mismatch on
  // one screen, the owner's report.
  //
  // D6 (OPEN-DEFECTS-20260922) — the fix above only closed the NEEDED case: once the agreement
  // stage's own state moves PAST NEEDED (the agency has signed, so it reads READY) but no *other*
  // stage has become NEEDED yet, `neededIndex` is -1 again and this fell back to the same stale
  // `current_stage` ordinal it was written to avoid — reproducing the identical mismatch on the far
  // side of the agreement stage instead of the near side ("STEP 1 OF 3 · AGENCY CREATED" next to a
  // stepper already showing "Agreement ✓"). `Day0HorizontalStepper`'s checkmark never had this
  // problem because it never reads position at all — it asks each stage's own `.state`. The fallback
  // below now does the same: the first stage NOT YET READY (BLOCKED, i.e. waiting on the platform).
  // `state.currentStage` is no longer read by this stage-index resolution — the null-stage early
  // return above (line ~175) still reads it, since a null `currentStage` is itself the "nothing has
  // started yet" signal this component renders honestly rather than coercing to a fake stage 1.
  const neededIndex = state.stages.findIndex((s) => s.state === 'NEEDED')
  const notReadyIndex = state.stages.findIndex((s) => s.state !== 'READY')
  // The `state.stages.length - 1` branch (every stage READY, or an empty array) is untested —
  // appears unreachable today since ReadinessGate.tsx only renders this board pre-approval, so a
  // fully-READY state shouldn't reach it in production. Kept as an honest last resort rather than
  // an assumed invariant, in case that gating ever changes.
  const activeStageIndex =
    neededIndex >= 0 ? neededIndex : notReadyIndex >= 0 ? notReadyIndex : state.stages.length - 1
  const currentStageData: AgencyActivationStage | null = state.stages[activeStageIndex] ?? null

  // Only the agreement stage has a wired action today (mirrors the pre-existing handleAction
  // switch, which only ever had one case). state === 'NEEDED' is the gate, never primaryAction
  // presence alone — a BLOCKED stage must never render a CTA regardless of what primaryAction
  // carries (data-invariant defence, pinned by test).
  const canSignAgreement =
    currentStageData?.key === 'agreement' &&
    currentStageData.state === 'NEEDED' &&
    !!currentStageData.actionId

  // ONE ACTION, NOT TWO (owner ruling 2026-09-07, see AgencyDay0Screen.tsx): the CTA lives on the
  // identity header exactly once, never duplicated into the next-step card too.
  const ctaLabel = canSignAgreement
    ? (AGENCY_CTA_LABELS[currentStageData!.primaryAction ?? ''] ?? currentStageData!.primaryAction ?? 'Review & sign')
    : null

  function handleCta() {
    if (canSignAgreement && currentStageData?.actionId) {
      navigate({ to: '/agreements/$agreementId', params: { agreementId: currentStageData.actionId } })
    }
  }

  // The agreement stage's OWN copy assumes NEEDED (something to sign). When it's the resolved
  // active stage but its state is anything else, "not actionable" has two different causes and
  // T0075 requires they read differently: not yet drafted (AGREEMENT_PREPARING_COPY) vs already
  // signed by this org and waiting on the platform (AGREEMENT_AWAITING_COUNTERSIGNATURE_COPY).
  const baseStageCopy =
    currentStageData === null
      ? FALLBACK_STAGE_COPY
      : currentStageData.key === 'agreement' && currentStageData.state !== 'NEEDED'
        ? (currentStageData.reason === 'AWAITING_COUNTERSIGNATURE'
            ? AGREEMENT_AWAITING_COUNTERSIGNATURE_COPY
            : AGREEMENT_PREPARING_COPY)
        : (AGENCY_STAGE_COPY[currentStageData.key] ?? FALLBACK_STAGE_COPY)

  // ADG-T02 — the mirror of the org's own "you've signed, waiting on the platform" state, which
  // T0075 gave its own copy above (AGREEMENT_AWAITING_COUNTERSIGNATURE_COPY). This is the OTHER
  // direction: the platform signed first. Disjoint from that branch by construction — this only
  // ever fires at `state === 'NEEDED'` (the still-live sign prompt), never at the BLOCKED states
  // AGREEMENT_AWAITING_COUNTERSIGNATURE_COPY/AGREEMENT_PREPARING_COPY cover, and BE's own precedence
  // rule means AWAITING_COUNTERSIGNATURE can only arise when the counterparty has NOT signed — so
  // the two notes can never need to appear together. PRESENCE IS EVIDENCE, ABSENCE IS NOT (see
  // `AgencyActivationStage.signatures`) — so this only ever ADDS a positive note onto the existing
  // sign prompt; an absent/empty list changes nothing, silently.
  const counterpartyAlreadySigned =
    currentStageData?.key === 'agreement' &&
    currentStageData.state === 'NEEDED' &&
    (currentStageData.signatures ?? []).some((s) => s.role === COUNTERPARTY_RECIPIENT_ROLE)

  const stageCopy = counterpartyAlreadySigned
    ? {
        ...baseStageCopy,
        footnoteText: [baseStageCopy.footnoteText, AGREEMENT_COUNTERPARTY_SIGNED_NOTE]
          .filter((s): s is string => !!s)
          .join(' '),
      }
    : baseStageCopy

  const stageLabel = currentStageData?.label ?? '—'

  return (
    <div data-testid="agency-activation-board" className="flex flex-col gap-6 p-6">
      <Day0IdentityHeader
        orgName={orgName}
        ownerName={null}
        createdAt={state.submittedAt}
        ctaLabel={ctaLabel}
        onCta={handleCta}
      />

      <Day0StepperCard
        stages={state.stages}
        currentStage={activeStageIndex + 1}
        // AgencyActivationState carries neither `waiting` nor `debug` — BE doesn't expose either
        // for agencies today. Omitting is honest; inventing a milestone or a last-transition chip
        // here is exactly the kind of fabrication this rebuild is meant to avoid.
        nextMilestone={null}
        lastTransition={null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-start gap-4">
        <Day0NextStepCard
          instructionHeading={stageCopy.instructionHeading}
          instructionBody={stageCopy.instructionBody}
          primaryAction={null}
          secondaryAction={null}
          secondaryUrl={null}
          tertiaryLinks={null}
          acceptedFormats={null}
        />
        <div className="flex flex-col gap-4">
          <Day0CurrentStageCard
            stageLabel={stageLabel}
            // AgencyActivationState's stages don't carry the sub-item granularity the client's
            // checklistRows need (e.g. "profile ready" / "inventory confirmed" as separate,
            // independently-derived facts), and unlike the client this is not a BE gap awaiting
            // future work — agencies have no checklist-row concept to build. Omitted (undefined),
            // not null: null renders "Coming in next update," which is wrong for something that
            // isn't coming (C13/T0062 — the owner's report named this placeholder explicitly).
            checklistItems={undefined}
            footnoteText={stageCopy.footnoteText}
          />
          <Day0PeopleCard />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 md:items-start gap-4">
        <Day0PostImportExplainer eyebrow="WHAT HAPPENS AFTER SIGNING?" steps={AGENCY_POST_SIGN_STEPS} />
        <Day0LiveDashboardTile />
      </div>
    </div>
  )
}
